import {
  useEffect, useRef, useState
} from 'react'
import {
  authService,
  getApiErrorMessage
} from '../../services/api'
import type {
  AssistantCharacter,
  CustomCharacterDetails
} from '../../services/api'
import {
  OnboardingWizardEvents,
  trackEvent
} from '../../analytics/posthog'
import {
  Check, Loader2, Pencil, Plus
} from 'lucide-react'
import {
  CustomCharacterForm
} from '../CustomCharacterForm'
import {
  TapToPickCard
} from './TapToPickCard'
import {
  useMobilePickerArm
} from './useMobilePickerArm'
import type {
  PresignupAssistantChoice
} from '../../utils/presignupAssistant'

type CharacterSelectionStepProps = {
  initialSelectedId?: number | null | undefined
} & (
  | { onSelect: (character: AssistantCharacter) => void; onChoose?: never }
  // A visitor with no account yet (GET-18726): hand the pick back unsaved.
  | { onChoose: (choice: PresignupAssistantChoice, assistantName: string) => void; onSelect?: never }
)

export function CharacterSelectionStep({
  onSelect,
  onChoose,
  initialSelectedId,
}: CharacterSelectionStepProps): JSX.Element {
  // The mobile picker experiment: 'test' commits on tap, with no preselection
  // and no Continue button.
  const pickerAssignment = useMobilePickerArm()
  const tapToPick = pickerAssignment.arm === 'test'
  const [characters, setCharacters] = useState<AssistantCharacter[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(
    tapToPick ? null : initialSelectedId ?? null,
  )
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showCustomForm, setShowCustomForm] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const tapErrorRef = useRef<HTMLParagraphElement>(null)

  // The wizard's funnel events describe signed-in users; the login page's
  // picker reports its own.
  const isWizard = onChoose == null

  useEffect(() => {
    if (isWizard) {
      trackEvent(OnboardingWizardEvents.CHARACTER_STEP_VIEWED, {
        picker_arm: pickerAssignment.arm,
        picker_enrolled: pickerAssignment.enrolled,
      })
    }
    void loadCharacters()
  }, [])

  const loadCharacters = async (): Promise<void> => {
    try {
      const chars = await authService.listAssistantCharacters()
      setCharacters(chars)
      if (tapToPick) return
      // Preselect a sensible default so the user can just hit Continue. The API
      // doesn't expose a default flag, but built-in characters come back first
      // (the custom one is appended last), so the first built-in is the default.
      setSelectedId((prev) => {
        if (prev != null) return prev
        const firstBuiltIn = chars.find((c) => !c.is_custom)
        return firstBuiltIn?.id ?? null
      })
    } catch (err) {
      console.error('Failed to load characters:', err)
      setError(
        getApiErrorMessage(err, "We couldn't load the assistant options. Please refresh and try again."),
      )
    } finally {
      setLoading(false)
    }
  }

  // Selecting a card only highlights it now — the user commits with Continue.
  const handleCardSelect = (character: AssistantCharacter): void => {
    setSelectedId(character.id)
    setError(null)
    if (!isWizard) return
    trackEvent(OnboardingWizardEvents.CHARACTER_SELECTED, {
      character_id: character.id,
      character_name: character.name,
    })
  }

  const handleContinue = async (): Promise<void> => {
    const character = characters.find((c) => c.id === selectedId)
    if (!character) return
    await commitCharacter(character)
  }

  const commitCharacter = async (character: AssistantCharacter): Promise<void> => {
    if (onChoose) {
      onChoose({ kind: 'builtin', character_id: character.id }, character.name)
      return
    }

    setSaving(true)
    try {
      await authService.updateAssistantCharacter(character.id)
      trackEvent(OnboardingWizardEvents.CHARACTER_STEP_COMPLETED, {
        character_id: character.id,
        character_name: character.name,
      })
      onSelect?.(character)
    } catch (err) {
      console.error('Failed to select character:', err)
      setError(
        getApiErrorMessage(err, "We couldn't save your choice. Please try again."),
      )
      // Nothing was committed, so nothing stays highlighted as if it had been.
      if (tapToPick) setSelectedId(null)
    } finally {
      setSaving(false)
    }
  }

  const handleCustomCharacterCreated = (character: AssistantCharacter): void => {
    // Defensive: never advance with a missing character. The save path only
    // calls this on success now, but a guard keeps a regression from crashing
    // the grid on `character.id` / a later `c.is_custom` access.
    if (!character) return
    setError(null)
    setShowCustomForm(false)
    // Add/replace custom character in list
    setCharacters((prev) => {
      const withoutCustom = prev.filter((c) => !c.is_custom)
      return [...withoutCustom, character]
    })
    setSelectedId(character.id)
    trackEvent(OnboardingWizardEvents.CHARACTER_STEP_COMPLETED, {
      character_id: character.id,
      character_name: character.name,
    })
    onSelect?.(character)
  }

  const handleCustomDetails = (details: CustomCharacterDetails): void => {
    setShowCustomForm(false)
    onChoose?.({ kind: 'custom', ...details }, details.name)
  }

  // In the test arm a tap is the whole choice: it highlights the card and
  // commits it straight away.
  const handleTapToPick = (character: AssistantCharacter): void => {
    if (character.is_custom) {
      setShowCustomForm(true)
      return
    }
    handleCardSelect(character)
    void commitCharacter(character)
  }

  // The grid runs past a phone's viewport in either direction, so bring a
  // failed tap's error to wherever the visitor has scrolled.
  useEffect(() => {
    if (tapToPick && error) tapErrorRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [tapToPick, error])

  const existingCustom = characters.find((c) => c.is_custom)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="animate-slide-in-right">
      <div className="text-center mb-8">
        <h2 className="text-3xl sm:text-4xl font-display uppercase tracking-tight text-gray-900 mb-3">
          Meet your assistant
        </h2>
        <p className="text-gray-500 text-lg">
          Choose who will help manage your schedule
        </p>
      </div>

      {tapToPick ? (
        <>
          {error && (
            <p ref={tapErrorRef} className="text-center text-sm text-red-600 mb-4 scroll-mt-4" role="alert">
              {error}
            </p>
          )}
          <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto">
            {characters.map((character) => (
              <TapToPickCard
                key={character.id}
                name={character.name}
                description={character.bio}
                pictureUrl={character.picture_url}
                actionLabel={character.is_custom ? 'Edit' : 'Choose'}
                selected={selectedId === character.id}
                disabled={saving}
                onTap={() => handleTapToPick(character)}
              />
            ))}
            {!existingCustom && (
              <TapToPickCard
                name="Create Your Own"
                description="Design your custom assistant"
                actionLabel="Create"
                disabled={saving}
                onTap={() => setShowCustomForm(true)}
              />
            )}
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:gap-5 max-w-lg mx-auto mb-8">
            {characters.map((character) => {
              const isSelected = selectedId === character.id
              return (
                <button
                  key={character.id}
                  onClick={() => {
                    if (character.is_custom) {
                      setShowCustomForm(true)
                    } else {
                      handleCardSelect(character)
                    }
                  }}
                  disabled={saving}
                  className={`relative flex flex-col items-center p-5 rounded-2xl border-2 transition-[border-color,box-shadow,transform] duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50/60 shadow-soft'
                      : 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-soft'
                  } ${saving ? 'opacity-70 cursor-wait' : 'cursor-pointer'}`}
                >
                  {isSelected && (
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center animate-scale-in">
                      <Check className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                  {character.is_custom && (
                    <div className="absolute top-2 right-2">
                      <Pencil className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                  )}
                  <div
                    className={`w-20 h-20 rounded-full overflow-hidden mb-3 ring-2 transition-[box-shadow] duration-200 ${
                      isSelected ? 'ring-primary-400 ring-offset-2' : 'ring-gray-100'
                    }`}
                  >
                    {character.picture_url ? (
                      <img
                        src={character.picture_url}
                        alt={character.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary-200 to-primary-300 flex items-center justify-center text-2xl font-bold text-primary-700">
                        {character.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">{character.name}</h3>
                  <p className="text-xs text-gray-500 mt-1 text-center leading-relaxed line-clamp-4">
                    {character.bio}
                  </p>
                </button>
              )
            })}

            {/* "Create Your Own" card — only shown if no custom character exists yet */}
            {!existingCustom && (
              <button
                onClick={() => setShowCustomForm(true)}
                disabled={saving}
                className={`relative flex flex-col items-center justify-center p-5 rounded-2xl border-2 border-dashed border-gray-300 transition-[border-color,background-color,box-shadow,transform] duration-200 hover:scale-[1.02] hover:border-primary-400 hover:bg-primary-50/30 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 ${saving ? 'opacity-70 cursor-wait' : 'cursor-pointer'}`}
              >
                <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <Plus className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm">Create Your Own</h3>
                <p className="text-xs text-gray-500 mt-1 text-center leading-relaxed">
                  Design a custom assistant
                </p>
              </button>
            )}
          </div>

          {error && (
            <p className="text-center text-sm text-red-600 mb-4" role="alert">
              {error}
            </p>
          )}

          <button
            onClick={() => void handleContinue()}
            disabled={selectedId == null || saving}
            className="w-full max-w-lg mx-auto flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold shadow-soft hover:shadow-soft-lg transition-[background-color,box-shadow,transform] duration-200 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Setting up your assistant...
              </>
            ) : (
              'Continue'
            )}
          </button>
        </>
      )}

      {showCustomForm && (
        <CustomCharacterForm
          onSuccess={handleCustomCharacterCreated}
          onClose={() => setShowCustomForm(false)}
          existingCharacter={existingCustom}
          onSubmitDetails={onChoose ? handleCustomDetails : undefined}
        />
      )}
    </div>
  )
}
